/**
 * Types and constants for onboarding functionality
 */

// Define valid category values as a union type
// if changing here, make sure to also change the ContentCategory enum in core/models/youtube_channel.py
export type ContentCategory =
  | 'Personal Growth & Productivity'
  | 'Mental Health & Mindfulness'
  | 'Physical Health & Fitness'
  | 'Entrepreneurship & Startups'
  | 'Finance & Investments'
  | 'Management & Leadership'
  | 'Marketing & Branding'
  | 'AI & Computer Science'
  | 'Space, Astronomy & Physics'
  | 'Biotech & Life Sciences'
  | 'Politics & Government'
  | 'History';

export interface User {
  feed_categories?: ContentCategory[];
  [key: string]: any;
}

export const ONBOARDING_ROUTES = {
  CATEGORY_SELECTION: '/onboarding',
  ITEM_RANKING: '/onboarding/rank-items',
} as const;

export const MIN_CATEGORIES_REQUIRED = 3;

/**
 * Category groups for the onboarding selection screen
 * These should match the ContentCategory type
 */
export const CATEGORY_GROUPS = [
  {
    title: 'Business, Career & Economy',
    categories: [
      'Entrepreneurship & Startups',
      'Finance & Investments',
      'Management & Leadership',
      'Marketing & Branding'
    ] as ContentCategory[]
  },
  {
    title: 'Science & Technology',
    categories: [
      'AI & Computer Science',
      'Space, Astronomy & Physics',
      'Biotech & Life Sciences'
    ] as ContentCategory[]
  },
  {
    title: 'Society, Politics & History',
    categories: [
      'Politics & Government',
      'History'
    ] as ContentCategory[]
  },
  {
    title: 'Self‐Improvement & Well‐Being',
    categories: [
      'Personal Growth & Productivity',
      'Mental Health & Mindfulness',
      'Physical Health & Fitness'
    ] as ContentCategory[]
  }
] as const;

// Type for category groups
export interface CategoryGroup {
  title: string;
  categories: ContentCategory[];
}

/**
 * Get all valid categories as a flat array
 * @returns Array of all valid categories
 */
export function getAllCategories(): ContentCategory[] {
  return CATEGORY_GROUPS.flatMap(group => group.categories);
}

/**
 * Validate if a category is a valid ContentCategory
 * @param category The category to validate
 * @returns boolean indicating if the category is valid
 */
export function isValidCategory(category: string): category is ContentCategory {
  return getAllCategories().includes(category as ContentCategory);
}

/**
 * Validate an array of categories
 * @param categories Array of categories to validate
 * @returns Object with validation result and any invalid categories found
 */
export function validateCategories(categories: string[]): {
  isValid: boolean;
  invalidCategories: string[];
  tooFew: boolean;
} {
  const invalidCategories = categories.filter(category => !isValidCategory(category));
  return {
    isValid: invalidCategories.length === 0 && 
            categories.length >= MIN_CATEGORIES_REQUIRED,
    invalidCategories,
    tooFew: categories.length < MIN_CATEGORIES_REQUIRED
  };
}

/**
 * Get the group title for a given category
 * @param category The category to find the group for
 * @returns The group title or undefined if not found
 */
export function getCategoryGroup(category: ContentCategory): string | undefined {
  const group = CATEGORY_GROUPS.find(group => 
    group.categories.includes(category)
  );
  return group?.title;
}

/**
 * Check if a user has completed the onboarding process
 * @param user The user object from authentication
 * @returns boolean indicating if onboarding is complete
 */
export function hasCompletedOnboarding(user: User | null): boolean {
  // Check if user has selected categories
  if (!user?.feed_categories) return false;
  
  const validation = validateCategories(user.feed_categories);
  if (!validation.isValid) return false;
  
  // Check if user has completed the item ranking step (at least 5 onboarding activities)
  // This will be checked on the backend, but we can add a flag in the future if needed
  // For now, we'll just check if categories are valid
  return true;
}

/**
 * Get the appropriate onboarding route based on user state
 * @param user The user object from authentication
 * @returns The route path for the next onboarding step
 */
export function getOnboardingRoute(user: User | null): string {
  // If user has no categories or invalid categories, send to category selection
  if (!user?.feed_categories) return ONBOARDING_ROUTES.CATEGORY_SELECTION;
  
  const validation = validateCategories(user.feed_categories);
  if (!validation.isValid) return ONBOARDING_ROUTES.CATEGORY_SELECTION;
  
  // If user has valid categories, send to item ranking
  return ONBOARDING_ROUTES.ITEM_RANKING;
}
